import React from "react";
import styled from "styled-components";
import { Table } from "@sonder/ui/source/styled/fragments/Table";
import { colors, sizes } from "@sonder/ui/source/styled/theme";
import {
  BuildingMetricsData,
  MetricHeadersData,
  MetricRowData,
} from "core/queries/GetBuildingsMetrics";
import { useAppSelector } from "core/store";
import { ReportsHeaderRow } from "reports/components/ReportsHeaderRow";
import { ReportsRow } from "reports/components/ReportsRow";
import { ReportType } from "reports/slice";
import { SortAscDesc } from "typings/globalGQLTypes";

const Container = styled.div`
  box-sizing: border-box;
  color: ${colors.gray1};
  max-width: ${sizes.listWidth};
  margin: 0 auto;
  padding: 1.5rem 1.714rem;
  width: 100%;
`;

interface ReportsListProps {
  metricTable: BuildingMetricsData;
}

const getColumnString = (metricHeaders: MetricHeadersData[]) => {
  const MAX_WIDTH_IN_REM = 70;
  const cellWidth = MAX_WIDTH_IN_REM / metricHeaders.length;

  return metricHeaders.reduce(acc => {
    acc = `${acc} ${cellWidth}rem`;
    return acc;
  }, "minmax(17rem, 1fr)");
};

const getFieldForSortColumn = (
  sortColumn: number,
  metricRow: MetricRowData,
) => {
  if (sortColumn === -1) return metricRow.resource.name;

  if (metricRow.metrics.length < sortColumn) return null;

  return metricRow.metrics[sortColumn].value;
};

export const ReportsList = ({ metricTable }: ReportsListProps) => {
  const buildingsSortInput = useAppSelector(
    state => state.reports.buildingsSortInput,
  );
  const individualsSortInput = useAppSelector(
    state => state.reports.individualsSortInput,
  );
  const reportType = useAppSelector(state => state.reports.reportType);

  const { metricHeaders, metricRows } = metricTable;
  const sortInput =
    reportType === ReportType.Buildings
      ? buildingsSortInput
      : individualsSortInput;
  const { column: sortColumn, order: sortOrder } = sortInput;
  const sortedMetricRows = metricRows.sort((rowA, rowB) => {
    const fieldA = getFieldForSortColumn(sortColumn, rowA);
    const fieldB = getFieldForSortColumn(sortColumn, rowB);

    if (fieldA === null || fieldB === null) {
      if (fieldA) return sortOrder === SortAscDesc.asc ? 1 : -1;

      if (fieldB) return sortOrder === SortAscDesc.asc ? -1 : 1;

      return 0;
    }

    if (fieldA > fieldB) return sortOrder === SortAscDesc.asc ? 1 : -1;

    if (fieldA < fieldB) return sortOrder === SortAscDesc.asc ? -1 : 1;

    return 0;
  });

  const renderReportRow = (metricRow: MetricRowData, index: number) => {
    const id = metricRow.resource.id;
    return <ReportsRow key={id || index} metricRow={metricRow} />;
  };

  return (
    <Container>
      <Table columns={getColumnString(metricHeaders)} rowHeight="4rem">
        <ReportsHeaderRow metricHeaders={metricHeaders} />
        {sortedMetricRows.map(renderReportRow)}
      </Table>
    </Container>
  );
};
